import React, { FC, useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { Delete } from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Slider
} from '@mui/material';
import StyledModal from '../Components/GenericModal/StyledModal';
import { sharedClasses } from '../Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../Components/GenericModal/ModalFooterButtons';
import {
  tableSearchButtonStyles,
  weightSliderStyles,
  centerAlignStyles
} from '../shared/commonStyles';
import { classes } from '../../../components/NewUI/Components/Modals/styles';

interface Condition {
  id?: number;
  content: string;
  weight: number;
  short_label?: string;
}

interface ConditionsDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  conditions: Condition[];
  onConditionsChange: (conditions: Condition[]) => void;
  onRefineScreening: () => void;
}

const ConditionsDialog: FC<ConditionsDialogProps> = ({
  isOpen,
  handleClose,
  conditions,
  onConditionsChange,
  onRefineScreening
}) => {
  const isSubmitDisabled = conditions.length === 0;
  const mainModalContentRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    window.requestAnimationFrame(() => {
      if (!mainModalContentRef.current) return;

      const element = mainModalContentRef.current;
      if (element.scrollHeight > element.clientHeight) {
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  };

  const addCondition = () => {
    onConditionsChange([...conditions, { weight: 1, content: '' }]);
    scrollToBottom();
  };

  const removeCondition = (index: number) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    onConditionsChange(newConditions);
  };

  const updateWeight = (index: number, value: number) => {
    const newConditions = [...conditions];
    newConditions[index].weight = value;
    onConditionsChange(newConditions);
  };

  const updateConditionValue = (index: number, field: keyof Condition, value: string | number) => {
    if (field === 'weight') {
      updateWeight(index, Number(value));
      return;
    }
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      [field]: value
    };
    onConditionsChange(newConditions);
  };

  const handleRefineScreening = () => {
    onRefineScreening();
    handleClose();
  };

  return (
    <StyledModal
      isOpen={isOpen}
      handleClose={handleClose}
      styleOverrides={{
        maxWidth: '1300px',
        maxHeight: '700px'
      }}
      label="conditions-dialog"
      modalTitle="Edit Conditions"
      subHeaderStyling={false}
    >
      <Box ref={mainModalContentRef} sx={{ ...sharedClasses.mainModalContent, height: '100%' }}>
        <Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '80%' }}>Condition</TableCell>
                  <TableCell style={{ width: '15%' }}>Weight</TableCell>
                  <TableCell style={{ width: '5%' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conditions.map((condition, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        value={condition.content}
                        onChange={({ target }) =>
                          updateConditionValue(index, 'content', target.value)
                        }
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={4}
                      />
                    </TableCell>
                    <TableCell>
                      <Slider
                        sx={{
                          ...classes.zoomSlider,
                          ...weightSliderStyles
                        }}
                        value={condition.weight}
                        onChange={(_, newValue) => {
                          if (typeof newValue === 'number') {
                            updateConditionValue(index, 'weight', newValue);
                          }
                        }}
                        valueLabelDisplay="auto"
                        step={1}
                        marks={[
                          { value: 1, label: '1' },
                          { value: 2, label: '' },
                          { value: 3, label: '' },
                          { value: 4, label: '' },
                          { value: 5, label: '5' }
                        ]}
                        min={1}
                        max={5}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={centerAlignStyles}>
                        <IconButton onClick={() => removeCondition(index)} color="error">
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        mt={2}
        sx={{ width: '100%' }}
      >
        <Button
          variant="contained"
          onClick={addCondition}
          sx={{ ...tableSearchButtonStyles, whiteSpace: 'nowrap' }}
        >
          Add Condition
        </Button>
        <ModalFooterButtons
          primaryButtonText="Refine Screening"
          primaryButtonCallback={handleRefineScreening}
          primaryButtonDisabled={isSubmitDisabled}
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          primaryButtonID="refine-screening-button"
          secondaryButtonID="cancel-conditions"
          stylesOverride={{ justifyContent: 'flex-end' }}
        />
      </Stack>
    </StyledModal>
  );
};

export default ConditionsDialog;
